import { Injectable, OnDestroy , inject} from '@angular/core';
import { AppConfigService } from 'app/core/config/app-config.service';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { AuthRepository } from 'app/core/auth/auth.repository';
import { VirtualClassroomService } from './vclassroom.service';

@Injectable({ providedIn: 'root' })
export class PresenceService implements OnDestroy {
  readonly appConfig = inject(AppConfigService);
  private readonly SESSION_KEY = 'presence_session_uuid';
  private readonly HEARTBEAT_MS = 60_000;
  private heartbeatSub: Subscription | null = null;
  private unloadBound = false;

  constructor(
    private _http: HttpClient,
    private _authRepository: AuthRepository,
    private _vclassroom: VirtualClassroomService
  ) {}

  startPresence(sessionUuidFromLogin?: string): void {
    const token = this._authRepository.getAccessToken();
    if (!token) {
      return;
    }

    const sessionUuid = sessionUuidFromLogin || this.getOrCreateSessionUuid();
    this.saveSessionUuid(sessionUuid);
    this.bindUnloadHandlers();

    this._http
      .post<any>(`${this.appConfig.get('serverUrl')}/v1/presence/login`, {
        session_uuid: sessionUuid,
        source: 'web',
        ...this.buildLivekitPayload(),
      })
      .subscribe({
        next: (res) => {
          const serverSessionUuid = res?.data?.session_uuid;
          if (serverSessionUuid) {
            this.saveSessionUuid(serverSessionUuid);
          }
        },
        error: () => {
          // best-effort only
        },
      });

    this.startHeartbeat();
  }

  stopPresence(sendLogout: boolean = true): void {
    this.stopHeartbeat();
    const sessionUuid = this.getSessionUuid();
    if (sendLogout && sessionUuid) {
      this._http
        .post<any>(`${this.appConfig.get('serverUrl')}/v1/presence/logout`, { session_uuid: sessionUuid })
        .subscribe({
          next: () => {},
          error: () => {},
        });
    }
    this.clearSessionUuid();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatSub = interval(this.HEARTBEAT_MS).subscribe(() => {
      const sessionUuid = this.getSessionUuid();
      if (!sessionUuid) {
        return;
      }
      this._http
        .post<any>(`${this.appConfig.get('serverUrl')}/v1/presence/heartbeat`, {
          session_uuid: sessionUuid,
          ...this.buildLivekitPayload(),
        })
        .subscribe({
          next: () => {},
          error: (err) => {
            // Session was swept/closed server-side — re-register transparently
            if (err?.status === 404) {
              this.clearSessionUuid();
              this.startPresence();
            }
          },
        });
    });
  }

  private stopHeartbeat(): void {
    if (this.heartbeatSub) {
      this.heartbeatSub.unsubscribe();
      this.heartbeatSub = null;
    }
  }

  private bindUnloadHandlers(): void {
    if (this.unloadBound) {
      return;
    }
    this.unloadBound = true;

    const sendBeaconLogout = () => {
      const token = this._authRepository.getAccessToken();
      const sessionUuid = this.getSessionUuid();
      if (!token && !sessionUuid) {
        return;
      }
      const payload = JSON.stringify({
        access_token: token,
        session_uuid: sessionUuid,
      });
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(`${this.appConfig.get('serverUrl')}/v5/presence/logout-beacon`, blob);
    };

    // Use beforeunload only — visibilitychange also fires on tab switch / minimize,
    // which would close the session while the user is still active.
    window.addEventListener('beforeunload', sendBeaconLogout);

    // pagehide covers mobile browsers and bfcache scenarios where beforeunload is suppressed
    window.addEventListener('pagehide', sendBeaconLogout);
  }

  private buildLivekitPayload(): { room_name: string | null; participant_identity: string | null } {
    return {
      room_name: this._vclassroom.getCurrentRoomName(),
      participant_identity: this._vclassroom.getIdentity() || null,
    };
  }

  private getSessionUuid(): string | null {
    return localStorage.getItem(this.SESSION_KEY);
  }

  private saveSessionUuid(value: string): void {
    localStorage.setItem(this.SESSION_KEY, value);
  }

  private clearSessionUuid(): void {
    localStorage.removeItem(this.SESSION_KEY);
  }

  private getOrCreateSessionUuid(): string {
    const existing = this.getSessionUuid();
    if (existing) {
      return existing;
    }
    return `sess-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  ngOnDestroy(): void {
    this.stopHeartbeat();
  }
}

